import { useState } from 'react';

import { autoFormaliseOffers } from '@api/autoFormaliseOffers';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { IconCalendarOutline } from '@iwoca/icons';
import { Card, Spinner } from '@iwoca/orion';
import IwRedirect from '@iwRouter/IwRedirect';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { getLastRepaymentDate } from './NewLoanEndedView.helpers';
import styles from './NewLoanEndedView.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import InternationalisedName from '../dashboardComponents/InternationalisedName';
import {
    CreateNewApplicationForm,
    TApplicationStatus
} from '../NewOutstandingView/CreateNewApplicationForm/CreateNewApplicationForm';

type TProps = { accountId: string };

const NewLoanEndedView = ({ accountId }: TProps) => {
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading,
        isError
    } = useQuery({
        ...getAccountsQueryConfig(stateKey)
    });

    if (isLoading || isError) return <Spinner />;

    const loanEndedAccount = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const { cashflows } = loanEndedAccount;
    const lastRepaymentDate = getLastRepaymentDate(cashflows!);

    return (
        <div className="col-span-full m-auto">
            <Card className={styles.loanSummaryHeader}>
                <h1
                    className={styles.welcomeTitle}
                    data-testid="new-loan-ended-view"
                >
                    <FormattedMessage
                        id="_.endedview.new_loan_ended_section.welcome_back"
                        defaultMessage="Welcome back, {name}"
                        values={{
                            name: <InternationalisedName />
                        }}
                    />
                </h1>
                <div className={styles.welcomeSubtitle}>
                    <IconCalendarOutline />
                    <p>
                        <FormattedMessage
                            id="_.endedview.new_loan_ended_section.subtitle"
                            defaultMessage="You finished repaying your last loan on {date}"
                            values={{
                                date: (
                                    <strong>
                                        {' '}
                                        <FormattedDate
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                            value={new Date(lastRepaymentDate!)}
                                        />
                                    </strong>
                                )
                            }}
                        />
                    </p>
                </div>
            </Card>
            <Card className={styles.loanSummaryBody}>
                <h2 className={styles.loanSummaryTitle}>
                    <FormattedMessage
                        id="_.endedview.new_loan_ended_section.loan_summary_action_title"
                        defaultMessage="Want to see what we can offer you this time?"
                    />
                </h2>
                <ul className={styles.loanSummaryItems}>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage
                            id="_.endedview.new_loan_ended_section.bulletpoint_one"
                            defaultMessage="Applying is free and takes less than 5 minutes"
                        />
                    </li>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage
                            id="_.endedview.new_loan_ended_section.bulletpoint_two"
                            defaultMessage="It won't affect your credit score"
                        />
                    </li>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage
                            id="_.endedview.new_loan_ended_section.bulletpoint_three"
                            defaultMessage="If approved, you are under no obligation to take the funds"
                        />
                    </li>
                </ul>
                <LoanEndedForm accountId={accountId} />
            </Card>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default NewLoanEndedView;

const LoanEndedForm = ({ accountId }: { accountId: string }) => {
    const [applicationStatus, setApplicationStatus] =
        useState<TApplicationStatus>(null);
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const handleSuccess = async () => {
        const { result } = await autoFormaliseOffers({
            stateKey
        });

        switch (result) {
            case 'FORMALISED':
                await queryClient.invalidateQueries({
                    queryKey: ['accounts']
                });
                await queryClient.invalidateQueries({
                    queryKey: ['application', stateKey]
                });
                setApplicationStatus('auto_approved');
                break;
            case 'DECLINED':
                await queryClient.invalidateQueries({
                    queryKey: ['application', stateKey]
                });
                setApplicationStatus('auto_declined');
                break;
            case 'DOCS_COMPLETE':
                setApplicationStatus('docs_complete');
                break;
            case 'DOCS_REQUIRED':
                setApplicationStatus('requires_documents');
                break;
        }
    };

    switch (applicationStatus) {
        case 'requires_documents':
            return <IwRedirect pathname="/apply-flexi-loan/" />;

        case 'auto_approved':
        case 'auto_declined':
        case 'docs_complete':
            return <IwRedirect pathname="/my-offers/" />;

        default:
            return (
                <CreateNewApplicationForm
                    accountId={accountId}
                    setApplicationStatus={setApplicationStatus}
                    amountFieldTitle={
                        <FormattedMessage
                            id="_.endedview.new_loan_ended_section.amount_input_title"
                            defaultMessage="How much would you like to borrow?"
                        />
                    }
                    turnoverFieldTitle={
                        <FormattedMessage
                            id="_.endedview.new_loan_ended_section.turnover_input_title"
                            defaultMessage="How much did your business turn over in the last 12 months?"
                        />
                    }
                    onSuccess={handleSuccess}
                />
            );
    }
};
